import { authHeader, ApiError } from "@/api/client";

/** ai-agent SSE chat. Events: thinking | proposal | error | done. */
export interface ChatEvent {
  type: "thinking" | "proposal" | "error" | "done";
  data: unknown;
}

/** One prior turn of plain text, threaded so Nara has conversational context. */
export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
}

export interface ChatBody {
  page_id: string;
  version_id?: string;
  mode?: "auto" | "copy" | "layout" | "component" | "insight";
  message: string;
  registry_spec?: unknown;
  history?: ChatTurn[];
}

/**
 * POST /ai/v1/chat and stream Server-Sent Events. Calls onEvent per parsed
 * event. Resolves when the stream ends. Abort via the optional signal.
 */
export async function streamChat(
  body: ChatBody,
  onEvent: (e: ChatEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const resp = await fetch("/ai/v1/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream", ...authHeader() },
    body: JSON.stringify(body),
    credentials: "include",
    ...(signal ? { signal } : {}),
  });
  if (!resp.ok || !resp.body) throw new ApiError(resp.status, await resp.text().catch(() => ""));

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";

  const flush = (chunk: string) => {
    // SSE record: lines "event: <type>" and "data: <json|text>" separated by \n\n
    let event: ChatEvent["type"] = "thinking";
    const dataLines: string[] = [];
    for (const line of chunk.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim() as ChatEvent["type"];
      else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
    }
    if (!dataLines.length) return;
    const raw = dataLines.join("\n");
    let data: unknown = raw;
    try {
      data = JSON.parse(raw);
    } catch {
      /* keep raw text */
    }
    onEvent({ type: event, data });
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    // normalize CRLF (sse-starlette uses \r\n) so record/line splitting is reliable
    buf += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n");
    let idx: number;
    while ((idx = buf.indexOf("\n\n")) !== -1) {
      const record = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      if (record.trim()) flush(record);
    }
  }
  if (buf.trim()) flush(buf);
}
